import { fromJS, Map, List } from 'immutable';
import * as reduxHelper from 'store/utils/reduxHelper';
import Activity from 'models/BusinessModelsMarketing/Activity';
import { byId } from 'store/utils/sortFunctions';
import types from './types';

export const initialState = fromJS({
  ...reduxHelper.applyReceiveInfo({
    data: { byId: {}, bySegment: {}, byTypeAndSegment: {} },
    errorMessage: '',
    updatePending: false,
  }),
});

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case types.ACTIVITIES_LIST_SUCCEEDED:
      // Merge into the existing state instead of replacing it: each product tab lists only its
      // own product's activities (optionally by type), and all mounted tabs share this store.
      // Deleted activities are already removed by ACTIVITY_DELETE_SUCCEEDED.
      return state.merge(
        reduxHelper.applyReceiveInfo({
          data: {
            // byId
            byId: action.payload.results.reduce(
              (prevData, activity) =>
                prevData.set(activity.id.toString(), new Activity(activity)).sort(byId),
              state.get('data').get('byId')
            ),

            // bySegment
            bySegment: action.payload.results.reduce((prevData, activity) => {
              const segmentActivities = prevData.get(activity.segment.toString(), new List());
              return prevData.set(
                activity.segment.toString(),
                segmentActivities
                  .filter(prevActivity => prevActivity.id !== activity.id)
                  .push(new Activity(activity))
                  .sort(byId)
              );
            }, state.get('data').get('bySegment')),

            // byTypeAndSegment
            byTypeAndSegment: action.payload.results.reduce((prevData, activity) => {
              const { type, segment } = activity; // Extract type (step: T/M/BOFU) and segment
              const typeData = prevData.get(String(type), new Map());
              const segmentActivities = typeData.get(String(segment), new List());
              return prevData.set(
                String(type),
                typeData.set(
                  String(segment),
                  segmentActivities
                    .filter(prevActivity => prevActivity.id !== activity.id)
                    .push(new Activity(activity))
                    .sort(byId)
                )
              );
            }, state.get('data').get('byTypeAndSegment')),
          },
        })
      );
    case types.ACTIVITY_FETCH_SUCCEEDED:
    case types.ACTIVITY_CREATE_SUCCEEDED:
    case types.ACTIVITY_UPDATE_SUCCEEDED:
      return (() => {
        const segmentId = action.payload.segment.toString();
        const segmentActivities = state
          .get('data')
          .get('bySegment')
          .get(segmentId, new List())
          .filter(activity => activity.id !== action.payload.id)
          .push(new Activity(action.payload))
          .sort(byId);

        const type = String(action.payload.type);
        const typeSegments = state.get('data').get('byTypeAndSegment').get(type, new Map());
        const typeSegmentActivities = typeSegments
          .get(segmentId, new List()) // [Act1, Act2, newActi]
          .filter(activity => activity.id !== action.payload.id)
          .push(new Activity(action.payload))
          .sort(byId);

        const updatedTypeSegments = typeSegments.set(segmentId, typeSegmentActivities);

        return state.merge(
          reduxHelper.applyRequestInfo({
            updatePending: false,
            data: {
              byId: state
                .get('data')
                .get('byId')
                .set(action.payload.id.toString(), new Activity(action.payload)),
              bySegment: state.get('data').get('bySegment').set(segmentId, segmentActivities),
              byTypeAndSegment: state
                .get('data')
                .get('byTypeAndSegment')
                .set(type, updatedTypeSegments),
            },
          })
        );
      })();
    case types.ACTIVITY_FETCH:
    case types.ACTIVITY_CREATE:
    case types.ACTIVITY_UPDATE:
      return state.merge(
        reduxHelper.applyRequestInfo({
          updatePending: true,
        })
      );
    case types.ACTIVITY_DELETE_SUCCEEDED:
      return (() => {
        const { id, segment, type } = action.payload;
        const segmentId = segment.toString();
        const activityId = id.toString();
        const activityType = String(type);

        const byTypeAndSegment = state.get('data').get('byTypeAndSegment');
        const typeSegments = byTypeAndSegment.get(activityType, new Map());
        const segmentActivities = typeSegments.get(segmentId, new List()).filter(
          activity => String(activity.id) !== activityId
        );

        const updatedTypeSegments = typeSegments.set(segmentId, segmentActivities);
        const updatedByTypeAndSegment = byTypeAndSegment.set(activityType, updatedTypeSegments);

        return state.merge(
          reduxHelper.applyRequestInfo({
            updatePending: false,
            data: {
              byId: state.get('data').get('byId').delete(activityId),
              bySegment: state
                .get('data')
                .get('bySegment')
                .set(
                  segmentId,
                  state
                    .get('data')
                    .get('bySegment')
                    .get(segmentId, new List())
                    .filter(activity => String(activity.id) !== activityId)
                ),
              byTypeAndSegment: updatedByTypeAndSegment,
            },
          })
        );
      })();

    case types.ACTIVITY_UPDATE_FAILED:
      return state.merge(
        reduxHelper.applyErrorInfo({
          updatePending: false,
          errorMessage: action.payload,
        })
      );
    case types.ACTIVITY_CLEAR_ERROR:
      return state.merge(reduxHelper.applyReceiveInfo());
    case types.ACTIVITY_FETCH_FAILED:
    case types.ACTIVITIES_LIST_FAILED:
    case types.ACTIVITY_DELETE_FAILED:
    case types.ACTIVITY_CREATE_FAILED:
      return state.merge(
        reduxHelper.applyErrorInfo({
          errorMessage: action.payload,
        })
      );
    default:
      return state;
  }
}
